import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { actions as growerActions } from '../store/dux/grower-dux';
import { actions as environmentActions } from '../store/dux/environment-dux';
import { get, login } from '../lib/api';
import { Placeholder } from './Placeholder';
import { Amplify } from './Amplify';

const Context = createContext();

export const useAuthContext = () => useContext(Context);

export function AuthProvider({ children }) {
  return (
    <Amplify>
      <Authenticated>{children}</Authenticated>
    </Amplify>
  );
}

function Authenticated({ children }) {
  const dispatch = useDispatch();
  const [isAuthenticating, setAuthenticating] = useState(true);
  const [currentUser, setCurrentUser] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(environmentActions.loadEnvironment());
    dispatch(growerActions.loadOperations());

    login()
      .then((user) => {
        setCurrentUser({ ...user, isAuthenticated: true, userType: user.role });
      })
      .catch(setError)
      .finally(setAuthenticating.bind(null, false));
  }, [dispatch]);

  if (error) throw error;

  useEffect(() => {
    if (currentUser.permissions) setPermissions(currentUser.permissions);
  }, [currentUser.permissions]);

  const refreshPermissions = useCallback((growerId) => {
    get('/permissions', { params: growerId ? { growerId } : {} }).then(
      setPermissions,
    );
  }, []);

  const user = useMemo(
    () => ({ ...currentUser, permissions }),
    [currentUser, permissions],
  );

  const state = useMemo(
    () => ({ currentUser: user, refreshPermissions }),
    [user, refreshPermissions],
  );

  return (
    <Context.Provider value={state}>
      {isAuthenticating ? <Placeholder /> : children}
    </Context.Provider>
  );
}
